import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getStaffMemberById } from "@/services/staff/staffQueryService";
import { useAuth } from "@/hooks/useAuth";
import StaffProfileForm from "@/components/staff/StaffProfileForm";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";
import DeleteStaffDialog from "@/components/staff/dialogs/DeleteStaffDialog";
import { Separator } from "@/components/ui/separator";

const StaffProfile = () => {
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const [staffMember, setStaffMember] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const loadStaffMember = async () => {
      if (!staffId) return;
      
      try {
        setIsLoading(true);
        const result = await getStaffMemberById(staffId);
        
        if (result) {
          setStaffMember(result);
        } else {
          toast.error("Staff member not found");
          navigate("/admin/staff");
        }
      } catch (error) {
        console.error("Error fetching staff member:", error);
        toast.error("Failed to load staff profile", {
          description: error instanceof Error ? error.message : "Unknown error"
        });
        navigate("/admin/staff");
      } finally {
        setIsLoading(false);
      }
    };

    loadStaffMember();
  }, [staffId, navigate]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleSaved = () => {
    setIsEditing(false);
    // Reload staff member data
    if (staffId) {
      getStaffMemberById(staffId).then(result => {
        if (result) {
          setStaffMember(result);
          toast.success("Staff profile updated successfully");
        }
      }).catch(error => {
        console.error("Error reloading staff member:", error);
      });
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleStaffDeleted = () => {
    toast.success("Staff member deleted successfully");
    navigate("/admin/staff");
  };

  // Format date of birth if available
  const formatBirthday = (birthday: string | null, month: number | null, day: number | null) => {
    if (birthday) return new Date(birthday).toLocaleDateString();
    if (month && day) return `${month}/${day}`;
    return "Not set";
  };

  // Format the staff information for display
  const renderStaffInfo = () => {
    if (!staffMember) return null;

    return (
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="flex-shrink-0">
            {staffMember.profile_photo_url ? (
              <img 
                src={staffMember.profile_photo_url} 
                alt={`${staffMember.first_name} ${staffMember.last_name}`}
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-semibold">
                {`${staffMember.first_name.charAt(0)}${staffMember.last_name.charAt(0)}`}
              </div>
            )}
          </div>
          
          <div>
            <h2 className="text-2xl font-bold">
              {staffMember.first_name} {staffMember.last_name}
            </h2>
            <p className="text-muted-foreground capitalize">{staffMember.role}</p>
          </div>
          
          <div className="ml-auto mt-2 md:mt-0">
            <Button onClick={handleEditClick}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit Profile
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Contact Information</h3>
            <div className="mt-2 space-y-2">
              <p><span className="font-medium">Email:</span> {staffMember.email || "Not set"}</p>
              <p><span className="font-medium">Phone:</span> {staffMember.phone || "Not set"}</p>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-muted-foreground">Employment Details</h3>
            <div className="mt-2 space-y-2">
              <p><span className="font-medium">Start Date:</span> {staffMember.start_date ? new Date(staffMember.start_date).toLocaleDateString() : "Not set"}</p>
              <p><span className="font-medium">Status:</span> {staffMember.is_active ? "Active" : "Inactive"}</p>
              <p><span className="font-medium">Birthday:</span> {formatBirthday(
                staffMember.birthday, 
                staffMember.birthday_month,
                staffMember.birthday_day
              )}</p>
            </div>
          </div>
        </div>

        <Separator className="my-6" />
        
        <Card className="bg-red-50 border-red-200 mt-8">
          <CardHeader>
            <CardTitle className="text-red-600">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Deleting this staff member will permanently remove all their records. This action cannot be undone.
            </p>
          </CardContent>
          <CardFooter>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClick}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Staff Member
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="mx-auto max-w-4xl">
        <CardHeader>
          <CardTitle>
            <Skeleton className="h-8 w-48" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4 items-center">
              <Skeleton className="h-24 w-24 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mx-auto max-w-4xl">
      <CardHeader>
        <CardTitle>Staff Profile</CardTitle>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <StaffProfileForm
            staffMember={staffMember}
            onSaved={handleSaved}
            onCancel={handleCancel}
          />
        ) : (
          renderStaffInfo()
        )}
      </CardContent>

      {staffId && staffMember && (
        <DeleteStaffDialog
          staffId={staffId}
          staffName={`${staffMember.first_name} ${staffMember.last_name}`}
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          onDelete={handleStaffDeleted}
        />
      )}
    </Card>
  );
};

export default StaffProfile; 